import { useEffect, useState } from 'react';
import { risk, RiskEvent, RiskConfig, RiskAction } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg } from '../utils';

const levelColor: Record<string, string> = {
  high: 'bg-coral/20 text-coral',
  medium: 'bg-yellow-500/20 text-yellow-300',
};
const statusColor: Record<string, string> = {
  open: 'bg-yellow-500/20 text-yellow-300',
  resolved: 'bg-green-500/20 text-green-300',
  false_positive: 'bg-edge text-gray-400',
};
const reasonLabel: Record<string, string> = {
  impossible_travel: 'Unmögliche Reise',
  new_country: 'Neues Land',
  after_failures: 'Nach Fehlversuchen',
  after_reset: 'Nach Passwort-Reset',
  new_network: 'Neues Netz',
  off_hours: 'Ungewohnte Uhrzeit',
  new_client: 'Neuer Client',
};
const actionLabel: Record<RiskAction, string> = {
  logout: 'Sitzungen beenden',
  'require-mfa': 'MFA erzwingen',
  disable: 'Konto sperren',
};

function fmt(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : '—';
}

/** Ort aus Stadt/Land/IP, was da ist. */
export function placeOf(e: Pick<RiskEvent, 'city' | 'country' | 'ip'>): string {
  const geo = [e.city, e.country].filter(Boolean).join(', ');
  return geo ? `${geo}${e.ip ? ` (${e.ip})` : ''}` : e.ip || '—';
}

export default function RiskPage() {
  const { me, hasRole } = useAuth();
  const canManage = me?.capability === 'tenant-admin' || hasRole('iga-admin');

  const [items, setItems] = useState<RiskEvent[]>([]);
  const [config, setConfig] = useState<RiskConfig | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await risk.list(showClosed ? {} : { status: 'open' });
      setItems(res.data.items);
      setConfig(res.data.config);
    } catch (err) {
      setError(errMsg(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClosed]);

  const run = async (fn: () => Promise<unknown>) => {
    setError('');
    setNotice('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const respond = (e: RiskEvent, action: RiskAction) =>
    run(async () => {
      if (!window.confirm(`${actionLabel[action]} für ${e.username || e.user_id}?`)) return;
      await risk.respond(e.id, action);
      setNotice(`${actionLabel[action]}: ausgeführt.`);
    });

  const resolve = (e: RiskEvent, falsePositive: boolean) =>
    run(async () => {
      const note = window.prompt(falsePositive ? 'Als Fehlalarm markieren – Notiz (optional):' : 'Als erledigt markieren – Notiz (optional):', '');
      if (note === null) return;
      await risk.resolve(e.id, { falsePositive, note });
    });

  const poll = () =>
    run(async () => {
      const res = await risk.poll();
      setNotice(`Poll: ${res.data.fetched} Ereignisse gelesen, ${res.data.inserted} neu, ${res.data.findings} Funde${res.data.errors.length ? `, Fehler: ${res.data.errors.join('; ')}` : ''}.`);
    });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Anmelderisiken</h2>
      <p className="text-sm text-gray-400">
        Jede erfolgreiche Anmeldung wird gegen die gelernte Baseline der Person bewertet (Land, Reise,
        Netz, Fehlversuche davor, Uhrzeit, Client). Ab {config?.thresholds.medium ?? 30} Punkten entsteht
        ein Fund, ab {config?.thresholds.high ?? 60} ein hoher; die betroffene Person wird benachrichtigt.
        {config && (
          <>
            {' '}
            Lernphase: {config.learningLogins} Anmeldungen · GeoIP:{' '}
            {config.geoip ? 'aktiv' : 'nicht konfiguriert (nur Netz-, Zeit- und Fehlversuch-Signale)'} ·
            automatische Reaktion bei hoch: {config.autoAction ? actionLabel[config.autoAction] : 'keine'}.
          </>
        )}
      </p>
      {error && <p className="text-coral">{error}</p>}
      {notice && <p className="text-green-300">{notice}</p>}

      <section className="card overflow-x-auto">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium text-coral">Funde</h3>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-400">
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              erledigte anzeigen
            </label>
            {canManage && (
              <button className="btn-ghost" onClick={poll}>
                Jetzt abfragen
              </button>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="p-2">Zeit</th>
              <th className="p-2">Person</th>
              <th className="p-2">Ort</th>
              <th className="p-2">Client</th>
              <th className="p-2">Stufe</th>
              <th className="p-2">Gründe</th>
              <th className="p-2">Status</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td className="p-2 text-gray-500" colSpan={8}>
                  Keine Funde.
                </td>
              </tr>
            )}
            {items.map((e) => (
              <tr key={e.id} className="border-t border-edge align-top">
                <td className="p-2 whitespace-nowrap">{fmt(e.event_time)}</td>
                <td className="p-2">
                  <div className="text-white">{e.username || e.user_id}</div>
                  <div className="text-xs text-gray-500">{e.user_id}</div>
                </td>
                <td className="p-2">{placeOf(e)}</td>
                <td className="p-2">{e.client_id || '—'}</td>
                <td className="p-2">
                  <span className={`rounded px-2 py-0.5 text-xs ${levelColor[e.level] ?? ''}`}>
                    {e.level} · {e.score}
                  </span>
                </td>
                <td className="p-2">
                  <ul className="space-y-0.5">
                    {e.reasons.map((r, i) => (
                      <li key={i} title={r.detail}>
                        <span className="text-white">{reasonLabel[r.code] ?? r.code}</span>{' '}
                        <span className="text-xs text-gray-500">+{r.weight} · {r.detail}</span>
                      </li>
                    ))}
                  </ul>
                </td>
                <td className="p-2">
                  <span className={`rounded px-2 py-0.5 text-xs ${statusColor[e.status] ?? ''}`}>{e.status}</span>
                  {e.actions.length > 0 && (
                    <div className="text-xs text-gray-500">
                      {e.actions.map((a, i) => (
                        <div key={i}>
                          {actionLabel[a.action] ?? a.action} {a.ok ? '✓' : `✗ ${a.error ?? ''}`}
                          {a.by.startsWith('system:') ? ' (automatisch)' : ''}
                        </div>
                      ))}
                    </div>
                  )}
                  {e.resolution_note && <div className="text-xs text-gray-500">{e.resolution_note}</div>}
                </td>
                <td className="p-2">
                  {canManage && e.status === 'open' && (
                    <div className="flex flex-col gap-1">
                      <button className="btn-ghost" onClick={() => respond(e, 'logout')}>
                        Sitzungen beenden
                      </button>
                      <button className="btn-ghost" onClick={() => respond(e, 'require-mfa')}>
                        MFA erzwingen
                      </button>
                      <button className="btn-ghost" onClick={() => respond(e, 'disable')}>
                        Konto sperren
                      </button>
                      <button className="btn-primary" onClick={() => resolve(e, false)}>
                        Erledigt
                      </button>
                      <button className="btn-ghost" onClick={() => resolve(e, true)}>
                        Fehlalarm
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
